import { useEffect } from 'react';

/**
 * Generic confirmation modal. Mirrors the overlay / safe-area / escape-key
 * behaviour of `JoinedGroupModal` so everything feels consistent. Used for
 * member-kick, leave-group, and delete-group confirmations in the groups UI.
 *
 * Click on backdrop OR Escape = cancel (never confirm — destructive actions
 * require an explicit click on the confirm button).
 */
export function ConfirmModal({
  title,
  body,
  confirmLabel,
  confirmVariant = 'primary',
  cancelLabel = 'Cancel',
  busy = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  confirmVariant?: 'primary' | 'danger';
  cancelLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onCancel]);

  const confirmClass =
    confirmVariant === 'danger'
      ? 'h-11 shrink-0 rounded-xl border border-[color:var(--color-danger)] px-4 text-sm font-semibold text-[color:var(--color-danger)] hover:bg-[color:var(--color-danger)]/10 disabled:pointer-events-none disabled:opacity-60'
      : 'h-11 shrink-0 rounded-xl bg-[color:var(--color-accent)] px-4 font-medium text-[color:var(--color-accent-fg)] disabled:pointer-events-none disabled:opacity-60';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Respect iOS notch / home indicator; never kiss the viewport edge on
        // a 320px device.
        padding:
          'max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom))',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[360px] p-5 sm:p-6"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div>
          <h2
            id="confirm-modal-title"
            className="break-words"
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              lineHeight: 1.25,
            }}
          >
            {title}
          </h2>
          <p
            style={{
              margin: '8px 0 0',
              fontSize: 14,
              color: 'var(--fg-muted)',
              lineHeight: 1.45,
            }}
          >
            {body}
          </p>
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="h-11 shrink-0 rounded-xl border border-[color:var(--color-border)] px-4 text-sm font-semibold hover:border-[color:var(--color-fg-muted)] disabled:pointer-events-none disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            autoFocus
            className={confirmClass}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
